import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const GeographicHeatmap = ({ title = "Geographic Performance", data, metric = 'revenue' }) => {
  const [selectedMetric, setSelectedMetric] = useState(metric);
  const [viewMode, setViewMode] = useState('states');

  const metrics = [
    { id: 'revenue', label: 'Revenue', format: 'currency' },
    { id: 'call_volume', label: 'Call Volume', format: 'number' },
    { id: 'erpc', label: 'eRPC', format: 'currency' },
    { id: 'conversion_rate', label: 'Conversion Rate', format: 'percentage' }
  ];

  const mockStateData = [
    { state: 'California', code: 'CA', revenue: 125000, call_volume: 2500, erpc: 50.00, conversion_rate: 0.15 },
    { state: 'Texas', code: 'TX', revenue: 98000, call_volume: 2100, erpc: 46.67, conversion_rate: 0.14 },
    { state: 'Florida', code: 'FL', revenue: 87500, call_volume: 1800, erpc: 48.61, conversion_rate: 0.16 },
    { state: 'New York', code: 'NY', revenue: 112000, call_volume: 2200, erpc: 50.91, conversion_rate: 0.17 },
    { state: 'Illinois', code: 'IL', revenue: 65000, call_volume: 1400, erpc: 46.43, conversion_rate: 0.13 },
    { state: 'Pennsylvania', code: 'PA', revenue: 58000, call_volume: 1250, erpc: 46.40, conversion_rate: 0.14 },
    { state: 'Ohio', code: 'OH', revenue: 52000, call_volume: 1100, erpc: 47.27, conversion_rate: 0.15 },
    { state: 'Georgia', code: 'GA', revenue: 48000, call_volume: 1000, erpc: 48.00, conversion_rate: 0.16 },
    { state: 'North Carolina', code: 'NC', revenue: 42000, call_volume: 900, erpc: 46.67, conversion_rate: 0.14 },
    { state: 'Michigan', code: 'MI', revenue: 38000, call_volume: 800, erpc: 47.50, conversion_rate: 0.15 }
  ];

  const formatValue = (value, format) => {
    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        })?.format(value);
      case 'percentage':
        return `${(value * 100)?.toFixed(1)}%`;
      case 'number':
        return new Intl.NumberFormat('en-US')?.format(value);
      default:
        return value;
    }
  };

  const getIntensityColor = (value, maxValue) => {
    const intensity = value / maxValue;
    const opacity = Math.max(0.1, intensity);
    return `rgba(30, 64, 175, ${opacity})`;
  };

  const currentMetric = metrics?.find(m => m?.id === selectedMetric);
  const maxValue = Math.max(...mockStateData?.map(item => item?.[selectedMetric]));
  const sortedData = [...mockStateData]?.sort((a, b) => b?.[selectedMetric] - a?.[selectedMetric]);

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-card">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">{title}</h3>
        
        <div className="flex items-center space-x-2">
          {/* Metric Selector */}
          <div className="flex items-center bg-muted rounded-lg p-1">
            {metrics?.map((metric) => (
              <button
                key={metric?.id}
                onClick={() => setSelectedMetric(metric?.id)}
                className={`px-3 py-1 text-xs font-medium rounded transition-smooth ${
                  selectedMetric === metric?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {metric?.label}
              </button>
            ))}
          </div>

          <Button variant="outline" size="sm" iconName="Download">
            Export
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Map Visualization */}
        <div className="lg:col-span-2">
          <div className="relative bg-muted/20 rounded-lg p-4 h-96 flex items-center justify-center">
            {/* Simplified US Map Representation */}
            <div className="text-center">
              <Icon name="Map" size={64} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-sm text-muted-foreground mb-2">Interactive US Map</p>
              <p className="text-xs text-muted-foreground">
                Showing {currentMetric?.label} by State
              </p>
              
              {/* Mock Map Legend */}
              <div className="mt-6 flex items-center justify-center space-x-4">
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-primary/20 rounded"></div>
                  <span className="text-xs text-muted-foreground">Low</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-primary/60 rounded"></div>
                  <span className="text-xs text-muted-foreground">Medium</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-primary rounded"></div>
                  <span className="text-xs text-muted-foreground">High</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Top Performers List */}
        <div className="space-y-4">
          <h4 className="font-medium text-foreground">Top Performing States</h4>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {sortedData?.slice(0, 10)?.map((item, index) => (
              <div
                key={item?.code}
                className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-6 h-6 bg-primary/10 text-primary text-xs font-medium rounded">
                    {index + 1}
                  </div>
                  <div>
                    <div className="font-medium text-foreground text-sm">{item?.state}</div>
                    <div className="text-xs text-muted-foreground">{item?.code}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-medium text-foreground text-sm">
                    {formatValue(item?.[selectedMetric], currentMetric?.format)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {item?.call_volume} calls
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Summary Stats */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="text-center p-4 bg-muted/20 rounded-lg">
          <div className="text-2xl font-bold text-foreground">
            {formatValue(
              mockStateData?.reduce((sum, item) => sum + item?.[selectedMetric], 0),
              currentMetric?.format
            )}
          </div>
          <div className="text-sm text-muted-foreground">Total {currentMetric?.label}</div>
        </div>
        
        <div className="text-center p-4 bg-muted/20 rounded-lg">
          <div className="text-2xl font-bold text-foreground">
            {formatValue(
              mockStateData?.reduce((sum, item) => sum + item?.[selectedMetric], 0) / mockStateData?.length,
              currentMetric?.format
            )}
          </div>
          <div className="text-sm text-muted-foreground">Average {currentMetric?.label}</div>
        </div>
        
        <div className="text-center p-4 bg-muted/20 rounded-lg">
          <div className="text-2xl font-bold text-foreground">
            {sortedData?.[0]?.state || 'N/A'}
          </div>
          <div className="text-sm text-muted-foreground">Top State</div>
        </div>
        
        <div className="text-center p-4 bg-muted/20 rounded-lg">
          <div className="text-2xl font-bold text-foreground">
            {mockStateData?.length}
          </div>
          <div className="text-sm text-muted-foreground">Active States</div>
        </div>
      </div>
    </div>
  );
};

export default GeographicHeatmap;